import type { DaypartBand, DaypartResponse, Suggestion, SuggestionArm, SuggestionReason, UpsellResponse } from "./types";

const REASONS: ReadonlySet<string> = new Set<SuggestionReason>(["PINNED", "CO_PURCHASE", "TIME_OF_DAY", "EXPLORE"]);
const ARMS: ReadonlySet<string> = new Set<SuggestionArm>(["EXPLOIT", "EXPLORE"]);
const BANDS: ReadonlySet<string> = new Set<DaypartBand>([
  "BREAKFAST",
  "LUNCH",
  "AFTERNOON",
  "DINNER",
  "LATE_NIGHT",
]);

/** Unknown/missing arm -> `"EXPLOIT"`, the safe fallback — never blocks rendering. */
function parseArm(raw: unknown): SuggestionArm {
  return typeof raw === "string" && ARMS.has(raw) ? (raw as SuggestionArm) : "EXPLOIT";
}

/** Missing/invalid `exploreSlots` -> `0` — no EXPLORE quota rather than an unbounded one. */
function parseExploreSlots(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw < 0) return 0;
  return Math.floor(raw);
}

/**
 * DEFENSIVE parser for one suggestion entry. Unexpected shapes are DROPPED
 * (never thrown) so one bad row cannot wipe the other seven — same policy as
 * `parseBestsellersResponse`.
 */
function parseSuggestion(raw: unknown): Suggestion | null {
  if (!raw || typeof raw !== "object") return null;

  const merchantItemId = (raw as { merchantItemId?: unknown }).merchantItemId;
  if (typeof merchantItemId !== "string" || merchantItemId.length === 0) return null;

  const reason = (raw as { reason?: unknown }).reason;
  if (typeof reason !== "string" || !REASONS.has(reason)) return null;

  const name = (raw as { name?: unknown }).name;
  const anchorItemId = (raw as { anchorItemId?: unknown }).anchorItemId;

  return {
    merchantItemId,
    name: typeof name === "string" ? name : "",
    reason: reason as SuggestionReason,
    arm: parseArm((raw as { arm?: unknown }).arm),
    ...(typeof anchorItemId === "string" && anchorItemId.length > 0 ? { anchorItemId } : {}),
  };
}

function parseSuggestionList(raw: unknown): Suggestion[] {
  if (!Array.isArray(raw)) return [];
  const parsed: Suggestion[] = [];
  for (const entry of raw) {
    const suggestion = parseSuggestion(entry);
    if (suggestion) parsed.push(suggestion);
  }
  return parsed;
}

/**
 * `GET .../suggestions/upsell` response. Never throws: any unexpected shape
 * becomes `{ enabled: true, suggestions: [] }`, the same as the gateway's own
 * fail-silent answer — an unparsable body must never read as "disabled".
 */
export function parseUpsellResponse(data: unknown): UpsellResponse {
  if (!data || typeof data !== "object") return { enabled: true, suggestions: [], exploreSlots: 0 };

  const enabled = (data as { enabled?: unknown }).enabled;
  if (enabled === false) return { enabled: false };

  return {
    enabled: true,
    suggestions: parseSuggestionList((data as { suggestions?: unknown }).suggestions),
    exploreSlots: parseExploreSlots((data as { exploreSlots?: unknown }).exploreSlots),
  };
}

/**
 * `GET .../suggestions/daypart` response. A missing/invalid `band` on an
 * otherwise-enabled response is treated as "no serving decision" (empty),
 * since the storefront titles the row from `band` and cannot show one
 * without it.
 */
const FALLBACK_DAYPART_RESPONSE: DaypartResponse = {
  enabled: true,
  band: "LATE_NIGHT",
  startHour: 0,
  endHour: 0,
  suggestions: [],
  exploreSlots: 0,
};

export function parseDaypartResponse(data: unknown): DaypartResponse {
  if (!data || typeof data !== "object") return FALLBACK_DAYPART_RESPONSE;

  const enabled = (data as { enabled?: unknown }).enabled;
  if (enabled === false) return { enabled: false };

  const band = (data as { band?: unknown }).band;
  if (typeof band !== "string" || !BANDS.has(band)) {
    return FALLBACK_DAYPART_RESPONSE;
  }

  const startHour = (data as { startHour?: unknown }).startHour;
  const endHour = (data as { endHour?: unknown }).endHour;

  return {
    enabled: true,
    band: band as DaypartBand,
    startHour: typeof startHour === "number" && Number.isFinite(startHour) ? startHour : 0,
    endHour: typeof endHour === "number" && Number.isFinite(endHour) ? endHour : 0,
    suggestions: parseSuggestionList((data as { suggestions?: unknown }).suggestions),
    exploreSlots: parseExploreSlots((data as { exploreSlots?: unknown }).exploreSlots),
  };
}
